import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { apiGet } from '@/lib/api/client';
import './PlaceSearchInput.css';

export interface PlaceSuggestion {
  id: string;
  placeId: string;
  primaryText: string;
  secondaryText: string;
  fullText: string;
}

interface PlaceSearchInputProps {
  placeholder?: string;
  onPlaceSelected?: (place: PlaceSuggestion) => void;
  onPlainSearch?: (query: string) => void;
  className?: string;
  showZipCode?: boolean;
  initialZipCode?: string;
  onZipCodeChange?: (zipCode: string) => void;
}

function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function PlaceSearchInput({
  placeholder = 'Search for places',
  onPlaceSelected,
  onPlainSearch,
  className = '',
  showZipCode = true,
  initialZipCode = '',
  onZipCodeChange,
}: PlaceSearchInputProps) {
  const [query, setQuery] = useState('');
  const [zipCode, setZipCode] = useState(initialZipCode);
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [sessionToken] = useState(generateUUID());
  const [isLoading, setIsLoading] = useState(false);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const updateDropdownPos = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
  }, []);

  // Geocode zip code to get coordinates
  useEffect(() => {
    if (!zipCode || zipCode.length < 5) {
      setLocation(null);
      return;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        // Use Google Geocoding API to convert zip code to coordinates
        const params = new URLSearchParams({
          address: zipCode,
        });

        const { data } = await apiGet<{
          location?: { lat: number; lng: number };
        }>(`/places/geocode?${params.toString()}`, {
          signal: controller.signal,
        });

        if (data.location) {
          setLocation(data.location);
          console.log('[PlaceSearch] Zip code geocoded:', zipCode, data.location);
        } else {
          setLocation(null);
        }
      } catch (error: any) {
        if (error.name !== 'AbortError') {
          console.error('[PlaceSearch] Failed to geocode zip:', error);
          setLocation(null);
        }
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [zipCode]);

  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      setActiveIndex(-1);
      return;
    }

    setIsLoading(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          input: query,
          sessionToken,
        });

        // Add location if available for location-biased results
        if (location) {
          params.set('lat', location.lat.toString());
          params.set('lng', location.lng.toString());
          params.set('radius', '20000'); // 20km radius
        }

        const { data } = await apiGet<{ predictions: PlaceSuggestion[] }>(
          `/places/autocomplete?${params.toString()}`,
          { signal: controller.signal }
        );

        setSuggestions(data.predictions ?? []);
        setActiveIndex(-1);
        setIsLoading(false);
        updateDropdownPos();
      } catch (error: any) {
        // Ignore abort errors
        if (error.name !== 'AbortError') {
          console.error('[PlaceSearch] Failed to fetch suggestions:', error);
        }
        setIsLoading(false);
      }
    }, 250);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [query, sessionToken, location]);

  // Close dropdown when clicking outside (use 'click' so portal onMouseDown fires first)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setSuggestions([]);
        setActiveIndex(-1);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        const selected = suggestions[activeIndex];
        handleSuggestionSelect(selected);
      } else if (query.trim()) {
        onPlainSearch?.(query);
      }
    } else if (e.key === 'Escape') {
      setSuggestions([]);
      setActiveIndex(-1);
    }
  };

  const handleSuggestionSelect = (suggestion: PlaceSuggestion) => {
    console.log('[PlaceSearch] Suggestion selected:', suggestion.primaryText, suggestion.placeId);
    onPlaceSelected?.(suggestion);
    setQuery(suggestion.primaryText);
    setSuggestions([]);
    setActiveIndex(-1);
  };

  const handleSuggestionClick = (idx: number) => {
    console.log('[PlaceSearch] Suggestion clicked, idx:', idx);
    const selected = suggestions[idx];
    handleSuggestionSelect(selected);
  };

  return (
    <div className={`place-search-wrapper ${className}`}>
      <div className="place-search-inputs">
        <div className="place-search-container" ref={containerRef}>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="form-input"
            onFocus={updateDropdownPos}
          />

          {suggestions.length > 0 && dropdownPos && createPortal(
            <div
              ref={dropdownRef}
              className="suggestions-dropdown xv-scroll"
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: Math.max(dropdownPos.width, 400),
                maxWidth: 600,
                zIndex: 99999,
              }}
            >
              {suggestions.map((s, idx) => (
                <div
                  key={s.id}
                  className={`suggestion-row ${idx === activeIndex ? 'is-active' : ''}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSuggestionClick(idx);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  <div className="suggestion-primary">{s.primaryText}</div>
                  {s.secondaryText && (
                    <div className="suggestion-secondary">{s.secondaryText}</div>
                  )}
                </div>
              ))}
            </div>,
            document.body
          )}

          {isLoading && query.length >= 2 && (
            <div className="place-search-loading">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ animation: 'spin 1s linear infinite' }}
              >
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" opacity="0.75" />
              </svg>
            </div>
          )}
        </div>

        {showZipCode && (
          <input
            type="text"
            value={zipCode}
            onChange={(e) => {
              const newZipCode = e.target.value.replace(/\D/g, '').slice(0, 5);
              setZipCode(newZipCode);
              onZipCodeChange?.(newZipCode);
            }}
            placeholder="ZIP"
            className="form-input zip-input"
            maxLength={5}
          />
        )}
      </div>
    </div>
  );
}
